import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useRef, useState, useId } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './AssessmentEditor.css';

export const QUESTION_TYPES = [
  { value: 'short-text', labelKey: 'assessmentEditor.types.shortText', icon: 'fa-solid fa-font' },
  { value: 'paragraph', labelKey: 'assessmentEditor.types.paragraph', icon: 'fa-solid fa-align-left' },
  { value: 'multiple-choice', labelKey: 'assessmentEditor.types.multipleChoice', icon: 'fa-solid fa-circle-dot' },
  { value: 'checkbox', labelKey: 'assessmentEditor.types.checkbox', icon: 'fa-solid fa-square-check' },
  { value: 'file-upload', labelKey: 'assessmentEditor.types.fileUpload', icon: 'fa-solid fa-file-arrow-up' },
  { value: 'record-video', labelKey: 'assessmentEditor.types.recordVideo', icon: 'fa-solid fa-video' },
];

export const VIDEO_ONLY_QUESTION_TYPES = QUESTION_TYPES.filter((type) => type.value === 'record-video');

/**
 * Normalize question identity and answer shape at the editor boundary.
 * `forceVideo` is reserved for legacy one-way interviews; two-way goals remain
 * text coverage targets and are never converted into candidate video prompts.
 */
export const normalizeQuestions = (questions = [], forceVideo = false) => (
  Array.isArray(questions) ? questions : []
).map((question, index) => ({
  ...question,
  id: question?.id || uuidv4().slice(0, 8),
  type: forceVideo ? 'record-video' : (question?.type || 'short-text'),
  text: question?.text || `Question ${index + 1}`,
  options: forceVideo ? null : (
    question?.type === 'multiple-choice' || question?.type === 'checkbox'
      ? (Array.isArray(question?.options) ? question.options : ['Option 1'])
      : null
  )
}));

/** Resolve a stable Font Awesome icon for legacy assessment answer formats. */
function getTypeIcon(type) {
  return QUESTION_TYPES.find((questionType) => questionType.value === type)?.icon || 'fa-solid fa-question';
}

/** Render a controlled question editor that grows to fit employer-authored text. */
function AutoResizeTextarea({ value, onChange, placeholder, className }) {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      rows={1}
    />
  );
}

/**
 * Shared assessment question/coverage-goal editor.
 *
 * Two-way AI interviews intentionally hide answer-format controls and present
 * rows as coverage goals. One-way AI interviews continue forcing record-video.
 */
const AssessmentQuestionBuilder = ({
  questions = [],
  onChange,
  t,
  isAiInterview = false,
  videoBannerText,
  isOneWayInterviewerBuilder = false,
}) => {
  useUiLanguage();
  const useInterviewerGoalControls = isAiInterview || isOneWayInterviewerBuilder;
  const forceVideo = isOneWayInterviewerBuilder;
  const [newQuestionType, setNewQuestionType] = useState('short-text');
  const builderId = useId();

  const addQuestion = (type) => {
    const nextType = forceVideo ? 'record-video' : (isAiInterview ? 'short-text' : type);
    const nextQuestions = [
      ...questions,
      {
        id: uuidv4().slice(0, 8),
        type: nextType,
        text: '',
        options: (nextType === 'multiple-choice' || nextType === 'checkbox') ? ['Option 1'] : null
      }
    ];

    onChange(nextQuestions);

    setTimeout(() => {
      const rows = document.querySelectorAll('[data-qidx]');
      rows[rows.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const updateQuestion = (idx, field, value) => {
    const nextQuestions = [...questions];
    const nextQuestion = { ...nextQuestions[idx], [field]: value };

    if (forceVideo) {
      nextQuestion.type = 'record-video';
      nextQuestion.options = null;
    } else if (field === 'type') {
      nextQuestion.options = (value === 'multiple-choice' || value === 'checkbox')
        ? (Array.isArray(nextQuestion.options) && nextQuestion.options.length ? nextQuestion.options : ['Option 1'])
        : null;
    }

    nextQuestions[idx] = nextQuestion;
    onChange(nextQuestions);
  };

  const removeQuestion = (idx) => {
    onChange(questions.filter((_, index) => index !== idx));
  };

  const moveQuestion = (fromIdx, direction) => {
    const toIdx = direction === 'up' ? fromIdx - 1 : fromIdx + 1;
    if (toIdx < 0 || toIdx >= questions.length) return;

    const nextQuestions = [...questions];
    [nextQuestions[fromIdx], nextQuestions[toIdx]] = [nextQuestions[toIdx], nextQuestions[fromIdx]];
    onChange(nextQuestions);

    setTimeout(() => {
      document.querySelectorAll('[data-qidx]')[toIdx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const addOption = (qIdx) => {
    const options = questions[qIdx].options || [];
    updateQuestion(qIdx, 'options', [...options, `Option ${options.length + 1}`]);
  };

  const updateOption = (qIdx, oIdx, value) => {
    const options = [...(questions[qIdx].options || [])];
    options[oIdx] = value;
    updateQuestion(qIdx, 'options', options);
  };

  const removeOption = (qIdx, oIdx) => {
    updateQuestion(qIdx, 'options', (questions[qIdx].options || []).filter((_, index) => index !== oIdx));
  };

  return (
    <div>
      {isAiInterview && (
        <div className="ae-video-banner" style={{ marginBottom: '18px' }}>
          <i className="fa-solid fa-circle-info"></i>
          <p>{videoBannerText || t('assessmentEditor.aiInterviewNote') || uiText("All questions in this AI interview are video-response questions.")}</p>
        </div>
      )}

      <div className="ae-section-header">
        <h3 className="ae-section-title">
          {t('assessmentEditor.questions', { count: questions.length })}
        </h3>
        <span className="ae-count-badge">{questions.length}</span>
      </div>

      <div className="ae-presets">
        {!useInterviewerGoalControls && (
          <label className="ae-format-field">
            <span className="ae-field-label">{t('assessmentEditor.newQuestionType')}</span>
            <select
              className="ae-select"
              value={newQuestionType}
              onChange={(event) => setNewQuestionType(event.target.value)}
            >
              {QUESTION_TYPES.map((questionType) => (
                <option key={questionType.value} value={questionType.value}>
                  {t(questionType.labelKey)}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          className={useInterviewerGoalControls ? 'ae-one-way-add-question-btn' : 'ae-btn-primary'}
          onClick={() => addQuestion(newQuestionType)}
        >
          <i className="fa-solid fa-circle-plus"></i>
          {t('newInterviewStep.addQuestion')}
        </button>
      </div>

      {questions.length === 0 ? (
        <div className="ae-empty-state">
          <i className="fa-solid fa-clipboard-question"></i>
          <span>{t('assessmentEditor.emptyState')}</span>
        </div>
      ) : (
        <div className="ae-question-list">
          {questions.map((question, idx) => (
            <div key={question.id} data-qidx={idx} className="ae-question-row" role="group" aria-label={t('assessmentEditor.questionLabel', { number: idx + 1 })}>
              <div className="ae-question-head">
                <div className="ae-question-handle">
                  <button type="button" onClick={() => moveQuestion(idx, 'up')} disabled={idx === 0} title={uiText("Move up")}>
                    <i className="fa-solid fa-chevron-up"></i>
                  </button>
                  <button type="button" onClick={() => moveQuestion(idx, 'down')} disabled={idx === questions.length - 1} title={uiText("Move down")}>
                    <i className="fa-solid fa-chevron-down"></i>
                  </button>
                </div>

                <div className="ae-question-meta">
                  <span className="ae-question-num">
                    {t('assessmentEditor.questionLabel', { number: idx + 1 })}
                  </span>
                  <span className="ae-question-type-badge">
                    <i className={isAiInterview && !forceVideo ? 'fa-solid fa-bullseye' : getTypeIcon(question.type)}></i>
                    {isAiInterview && !forceVideo
                      ? t('assessmentEditor.coverageGoal')
                      : (QUESTION_TYPES.some((type) => type.value === question.type)
                        ? t(QUESTION_TYPES.find((type) => type.value === question.type).labelKey)
                        : question.type)}
                  </span>
                </div>

                <div className="ae-question-actions">
                  <button type="button" className="ae-delete-btn" onClick={() => removeQuestion(idx)} title={uiText("Remove question")}>
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>

              <div className="ae-question-body">
                <div className={`ae-question-row-grid ${useInterviewerGoalControls ? 'ae-question-row-grid-one-way' : ''}`}>
                  <AutoResizeTextarea
                    value={question.text}
                    onChange={(event) => updateQuestion(idx, 'text', event.target.value)}
                    placeholder={t('assessmentEditor.questionPlaceholder')}
                    className="ae-textarea"
                  />
                  {!useInterviewerGoalControls && (
                    <div className="ae-format-field">
                      <label className="ae-field-label" htmlFor={`${builderId}-type-${question.id}`}>
                        {t('assessmentEditor.typeLabel')}
                      </label>
                      <select
                        id={`${builderId}-type-${question.id}`}
                        aria-label={`${t('assessmentEditor.questionLabel', { number: idx + 1 })}: ${t('assessmentEditor.typeLabel')}`}
                        className="ae-select"
                        value={question.type}
                        onChange={(event) => updateQuestion(idx, 'type', event.target.value)}
                      >
                        {!QUESTION_TYPES.some((type) => type.value === question.type) && (
                          <option value={question.type}>{question.type}</option>
                        )}
                        {QUESTION_TYPES.map((questionType) => (
                          <option key={questionType.value} value={questionType.value}>
                            {t(questionType.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {(question.type === 'multiple-choice' || question.type === 'checkbox') && (
                  <div>
                    <span className="ae-options-label">{t('assessmentEditor.answerOptions')}</span>
                    {(question.options || []).map((option, optionIndex) => (
                      <div key={optionIndex} className="ae-option-row">
                        <div className="ae-option-icon">
                          <i className={question.type === 'multiple-choice' ? 'fa-regular fa-circle' : 'fa-regular fa-square'}></i>
                        </div>
                        <input
                          type="text"
                          className="ae-option-input"
                          value={option}
                          onChange={(event) => updateOption(idx, optionIndex, event.target.value)}
                          placeholder={`Option ${optionIndex + 1}`}
                        />
                        <button type="button" className="ae-option-remove" onClick={() => removeOption(idx, optionIndex)}>
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    ))}
                    <button type="button" className="ae-add-option-btn" onClick={() => addOption(idx)}>
                      <i className="fa-solid fa-plus"></i>
                      {t('assessmentEditor.addOption')}
                    </button>
                  </div>
                )}

                {forceVideo && question.type === 'record-video' && (
                  <div className="ae-video-banner">
                    <i className="fa-solid fa-video"></i>
                    <p>{t('assessmentEditor.videoInfo.description')}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssessmentQuestionBuilder;
